export type GeoNorgeAddress = {
	gate?: string;
	husnummer?: string;
	bokstav?: string;
	postnummer?: string;
	poststed?: string;
	resultat?: string;
	pos?: {
		lat: number;
		lon: number;
	};
};

type AddressSearchObject = {
	region?: string;
	postal_code?: string;
	street_address?: string;
};

type SearchInput = string | AddressSearchObject;

type SearchCallback = (addresses: GeoNorgeAddress[]) => void;

interface GeoNorgeApiResponse {
	adresser?: {
		adressetekst: string;
		postnummer: string;
		poststed: string;
		adressenavn: string;
		nummer?: number;
		bokstav?: string;
		representasjonspunkt: {
			lat: number;
			lon: number;
		};
	}[];
}

// Kopiert direkte fra Mitt NTE
export default async function searchAddress(search: SearchInput, callback: SearchCallback): Promise<void> {
	// Documentation: https://ws.geonorge.no/adresser/v1/#/default/get_sok
	try {
		const queryObject: { [key: string]: string | number } = {};

		if (typeof search === "string") {
			queryObject.sok = search + "*";
		} else {
			if (search?.region) queryObject.poststed = search.region;
			if (search?.postal_code) queryObject.postnummer = search.postal_code;

			const address = search?.street_address?.match(/\r?\n/g) ? search?.street_address?.split(/\r?\n/g)[0]?.trim() : search?.street_address;
			if (address) {
				const parts = address.split(",");
				const numberMatch = parts?.length > 0 ? parts[0]?.match(/\d/g) : address.match(/\d/g);
				const number = numberMatch ? numberMatch.join("") : "";

				if (number) {
					queryObject.nummer = parseInt(number, 10);
				}

				const streetParts = parts?.length ? parts[0]?.split(number) : number ? address.split(number) : [address];
				queryObject.adressenavn = streetParts?.length > 0 ? streetParts[0].trim() : address.trim();

				const letter = streetParts?.length > 1 ? streetParts?.pop()?.trim() : "";
				if (letter) {
					queryObject.bokstav = letter;
				}
			}
		}

		const queryString = new URLSearchParams({
			...queryObject,
			utkoordsys: "4258",
			asciiKompatibel: "false",
			treffPerSide: "3",
			objtype: "Vegadresse",
			side: "0",
		}).toString();

		const response = await fetch(`https://ws.geonorge.no/adresser/v1/sok?${queryString}`);
		const json: GeoNorgeApiResponse = await response.json();

		if (!json?.adresser?.length) {
			throw new Error("No address found");
		}

		const addressArray: GeoNorgeAddress[] = json.adresser.map((address) => ({
			resultat: `${address.adressetekst}, ${address.postnummer} ${address.poststed}`,
			gate: address.adressenavn,
			husnummer: (address.nummer?.toString() ?? "") + (address.bokstav || ""),
			postnummer: address.postnummer,
			poststed: address.poststed,
			pos: {
				lat: address.representasjonspunkt.lat,
				lon: address.representasjonspunkt.lon,
			},
		}));

		const sortedAddressArray = addressArray.toSorted((a, b) => (a.resultat ?? "").localeCompare(b.resultat ?? ""));

		callback(sortedAddressArray);
	} catch (error) {
		console.error(error);
		callback([]);
	}
}
